import { Theme } from '@eversports/react-components/design-tokens/theme'
import Collapse from '@eversports/react-components/primitives/Collapse'
import Flex from '@eversports/react-components/primitives/Flex'
import Icon, { Icons } from '@eversports/react-components/primitives/Icon'
import Text, { SimpleText } from '@eversports/react-components/primitives/Text'
import { makeStyles } from '@mui/styles'
import React, { useState } from 'react'

import { uniqueBy } from '@eversports/lists'
import { Localized } from '../../localization/react'
import Activity from '../../typings/Activity'
import {
  ActivityUsage,
  getRenewalIntervalType,
  Interval,
  isRenewalIntervalShorterThanValidity,
  UnlimitedUsage,
} from '../../typings/Product'

import AccordionItem from './Accordion'

const useStyles = makeStyles((theme: Theme) => ({
  pointer: {
    cursor: 'pointer',
  },
  activityListItem: {
    display: 'list-item',
    listStyleType: 'disc',
    marginLeft: theme.layoutSpacing.default,
  },
  renewalBadge: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: theme.borderRadii.large,
    boxSizing: 'border-box',
    color: theme.palette.primary.main,
    display: 'inline-flex',
    fontSize: theme.fontSizing['x-small'],
    fontWeight: 'normal',
    height: theme.layoutSpacing.wide,
    padding: `${theme.layoutSpacing.dense} ${theme.layoutSpacing.default}`,
    textTransform: 'uppercase',
  },
}))

const UsageSummary = (activityUsage: ActivityUsage): React.ReactNode => (
  <Localized
    id="activity-usage-limit-summary"
    params={{
      usageLimit: activityUsage.usageLimit,
      unit: activityUsage.unit as any,
      intervalType: (activityUsage.repeating?.interval.type ?? '') as any,
    }}
  />
)

const RecordingsCountItem: React.FC<{ recordingsCount: number | null; className: string }> = ({
  recordingsCount,
  className,
}) => {
  if (!recordingsCount) return null

  return (
    <Text size="small" className={className}>
      <Localized id="activity-usage-limits-recordings" params={{ recordingsCount }} />
    </Text>
  )
}

const AccordionActivityList: React.FC<{
  activities: Array<Pick<Activity, 'id' | 'name' | 'venue'>>
  recordingsCount: number | null
  hasMultipleVenues: boolean
}> = ({ activities, recordingsCount, hasMultipleVenues }) => {
  const classes = useStyles()

  return (
    <>
      {activities.map((activity) => (
        <div key={activity.id} className={classes.activityListItem}>
          <Text size="small">{activity.name}</Text>
          {hasMultipleVenues && (
            <Text size="x-small" color="darkGray">
              {activity.venue.name}
            </Text>
          )}
        </div>
      ))}
      <RecordingsCountItem recordingsCount={recordingsCount} className={classes.activityListItem} />
    </>
  )
}

interface Props {
  activities: Array<Activity>
  activityUsages?: Array<ActivityUsage>
  unlimitedUsages?: Array<UnlimitedUsage>
  validityDuration?: Interval | null
}

const ValidActivities: React.FC<React.PropsWithChildren<Props>> = ({
  activities,
  activityUsages = [],
  unlimitedUsages = [],
  validityDuration = null,
}) => {
  const classes = useStyles()
  const [isCollapsed, setIsCollapsed] = useState(true)

  if (activityUsages.length > 0 || unlimitedUsages.length > 0) {
    const renewalIntervalType = getRenewalIntervalType(activityUsages)
    const isRenewalShorterThanValidity = isRenewalIntervalShorterThanValidity(activityUsages, validityDuration)
    const allActivitiesFromUsages = [
      ...activityUsages.flatMap((activityUsage) => activityUsage.activities),
      ...unlimitedUsages.flatMap((unlimitedUsage) => unlimitedUsage.activities),
    ]
    const hasMultipleVenues = uniqueBy(allActivitiesFromUsages, (activity) => activity.venue.id).length > 1

    return (
      <Flex flexDirection="column" verticalSpacing="dense">
        <Text size="small" fontWeight="bold">
          <Localized id="valid-activities" />
        </Text>
        {renewalIntervalType && (
          <span className={classes.renewalBadge}>
            <Localized id="abpr-renewal-badge" params={{ intervalType: renewalIntervalType as any }} />
          </span>
        )}
        {isRenewalShorterThanValidity && (
          <Flex alignItems="center" horizontalSpacing="dense">
            <Icon icon={Icons.RENEW} color="subtleText" size="small" />
            <Text size="small" color="darkGray">
              <Localized id="abpr-units-renew-caption" params={{ intervalType: renewalIntervalType as any }} />
            </Text>
          </Flex>
        )}
        {activityUsages.map((activityUsage) => {
          const summary = UsageSummary(activityUsage)

          return (
            <AccordionItem
              key={activityUsage.activities.map((activity) => activity.id).join('-')}
              title={activityUsage.name || summary}
              subtitle={activityUsage.name ? summary : undefined}
            >
              <AccordionActivityList
                activities={activityUsage.activities}
                recordingsCount={activityUsage.recordingsCount}
                hasMultipleVenues={hasMultipleVenues}
              />
            </AccordionItem>
          )
        })}
        {unlimitedUsages.map((unlimitedUsage) => (
          <AccordionItem
            key={unlimitedUsage.activities.map((activity) => activity.id).join('-')}
            title={unlimitedUsage.name}
            subtitle={<Localized id="unlimited-membership-usage" />}
          >
            <AccordionActivityList
              activities={unlimitedUsage.activities}
              recordingsCount={unlimitedUsage.recordingsCount}
              hasMultipleVenues={hasMultipleVenues}
            />
          </AccordionItem>
        ))}
      </Flex>
    )
  }

  const firstThreeActivities = activities.slice(0, 3)
  const remainingActivities = activities.slice(3, activities.length)

  const handleClick = () => {
    setIsCollapsed(!isCollapsed)
  }

  return (
    <div>
      <Text size="small">
        <Localized id="valid-activities" />
      </Text>
      {activities.length === 0 && (
        <Text>
          <Localized id="empty-activities" />
        </Text>
      )}
      {firstThreeActivities.map((activity) => (
        <Text key={activity.id}>{activity.name}</Text>
      ))}
      {remainingActivities.length > 0 && (
        <>
          <Collapse in={!isCollapsed}>
            {remainingActivities.map((activity) => (
              <SimpleText size="large" key={activity.id}>
                {activity.name}
              </SimpleText>
            ))}
          </Collapse>
          <Text color="secondary.main" className={classes.pointer} onClick={handleClick}>
            {isCollapsed ? <Localized id="show-more" /> : <Localized id="show-less" />}
          </Text>
        </>
      )}
    </div>
  )
}

export default ValidActivities
